import React, { useState, useEffect, useCallback } from 'react';
import {
    Tablet, Plus, Power, Copy, Check, KeyRound, X, RefreshCw, Trash2, Ban, ListChecks,
} from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../store/useStore';
import '../styles/Dispositivi.css';

/**
 * Tablet — la schermata di Amministrazione con due linguette.
 *
 * Due cose si facevano solo da riga di comando, e si sono scoperte
 * entrambe perche' si sono rotte con un paziente davanti: creare o
 * rigenerare la chiave di un dispositivo, e sbloccare un'accettazione
 * rimasta a meta'.
 *
 * DISPOSITIVI: elenco con l'ULTIMO CONTATTO - il Tablet 2 e' rimasto
 * fermo dalle 10:40 del 21 settembre 2026 e lo si e' scoperto leggendo
 * i log di Nginx; una colonna qui l'avrebbe detto a chiunque. Creare,
 * rigenerare la chiave, disattivare. La chiave si vede UNA volta.
 *
 * COMPILAZIONI IN SOSPESO: le sessioni aperte dai tablet - che sono
 * quelle che tengono un tablet fermo - le accettazioni depositate e non
 * confermate, i questionari a meta'. Annullare libera il tablet al giro
 * di polling successivo; cancellare toglie quelle gia' chiuse male.
 * Una compilazione CON DATI si cancella solo con conferma esplicita, e
 * nel registro resta chi e quando, non cosa conteneva.
 */

const quando = (v) => (v ? new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : 'mai');

/* «Fermo da» in parole: e' la colonna che avrebbe detto del tablet
   fermo. Sotto i due minuti e' vivo (il polling e' ogni quattro
   secondi); oltre, si scrive da quanto. */
const contatto = (v) => {
    if (!v) return { testo: 'mai', classe: 'ko' };
    const min = Math.round((Date.now() - new Date(v).getTime()) / 60000);
    if (min < 2) return { testo: 'adesso', classe: 'ok' };
    if (min < 60) return { testo: `fermo da ${min} min`, classe: 'ko' };
    if (min < 60 * 48) return { testo: `fermo da ${Math.round(min / 60)} ore`, classe: 'ko' };
    return { testo: `fermo da ${Math.round(min / 1440)} giorni`, classe: 'ko' };
};

const STATO_COMP = {
    in_attesa: 'in attesa', aperta: 'aperta sul tablet', annullata: 'annullata', scaduta: 'scaduta',
    scartata: 'scartata', annullato: 'annullato', scaduto: 'scaduto',
};
const APERTE = ['in_attesa', 'aperta'];

/* ---------- linguetta 1: dispositivi ---------- */

const Dispositivi = ({ isAdmin, setErrore, setAvviso }) => {
    const [dispositivi, setDispositivi] = useState([]);
    const [nuovo, setNuovo] = useState(null);
    const [rigenera, setRigenera] = useState(null);
    const [creato, setCreato] = useState(null);
    const [copiata, setCopiata] = useState(false);
    const [inCorso, setInCorso] = useState(false);

    const carica = useCallback(async () => {
        try {
            const r = await api.get('/firma-tablet/dispositivi');
            setDispositivi(r.data.data.dispositivi || []);
        } catch (e) { setErrore(e?.message || 'Non riesco a leggere i dispositivi.'); }
    }, [setErrore]);

    useEffect(() => { carica(); }, [carica]);
    /* L'ultimo contatto si aggiorna da solo ogni 15 secondi: e' una
       colonna che serve mentre la si guarda. */
    useEffect(() => { const t = setInterval(carica, 15000); return () => clearInterval(t); }, [carica]);

    const crea = async () => {
        if (!nuovo?.nome?.trim()) return;
        try {
            setErrore(null); setInCorso(true);
            const r = await api.post('/firma-tablet/dispositivi', {
                nome: nuovo.nome.trim(), tipo: nuovo.tipo || 'tablet', note: nuovo.note || null,
            });
            setCreato({ nome: r.data.data.dispositivo.nome, chiave: r.data.data.dispositivo.chiave, rigenerata: false });
            setCopiata(false); setNuovo(null);
            await carica();
        } catch (e) { setErrore(e?.message || 'Errore nella creazione'); } finally { setInCorso(false); }
    };

    const confermaRigenera = async () => {
        try {
            setErrore(null); setInCorso(true);
            const r = await api.post(`/firma-tablet/dispositivi/${rigenera.id}/rigenera`);
            setCreato({ nome: r.data.data.dispositivo.nome, chiave: r.data.data.dispositivo.chiave, rigenerata: true });
            setCopiata(false); setRigenera(null);
            await carica();
        } catch (e) { setErrore(e?.message || 'Errore nella rigenerazione'); } finally { setInCorso(false); }
    };

    /* «RIPRENDI COMPILAZIONE».
       Il comando sta QUI, alla postazione, e non sul tablet: se lo
       potesse chiedere il tablet, chi lo prende in mano dopo qualcun
       altro potrebbe farsi rimandare la compilazione di quella persona.
       Qui si vede che su un apparecchio c'e' una compilazione lasciata a
       meta' e a che passaggio e' arrivata; cosa ci sia scritto dentro no,
       e non serve saperlo per premere un pulsante. */
    const riprendi = async (d) => {
        try {
            setErrore(null);
            const r = await api.post(`/firma-tablet/dispositivi/${d.id}/riprendi`);
            setAvviso(r.data.message);
            await carica();
        } catch (e) { setErrore(e?.response?.data?.message || 'Non riesco a far riprendere la compilazione'); }
    };

    const buttaBozza = async (d) => {
        if (!window.confirm(
            `Annullare la compilazione lasciata a metà su «${d.nome}»? `
            + 'Quello che la persona aveva scritto viene cancellato e non si recupera.')) return;
        try {
            setErrore(null);
            const r = await api.delete(`/firma-tablet/dispositivi/${d.id}/bozza`);
            setAvviso(r.data.message);
            await carica();
        } catch (e) { setErrore(e?.response?.data?.message || 'Non riesco ad annullare la compilazione'); }
    };

    const disattiva = async (d) => {
        /* Il testo dice che non si torna indietro, perche' dal 23 settembre
           2026 non si torna indietro davvero: la chiave viene annullata, non
           sospesa. Prima diceva solo «la sua chiave non vale piu'», che si
           legge come una cosa reversibile - e chi disattiva il tablet
           sbagliato deve saperlo PRIMA di premere, non dopo. */
        if (!window.confirm(
            `Disattivare «${d.nome}»? La sua chiave viene annullata: non si può riattivare, `
            + 'per rimetterlo in servizio si crea un dispositivo nuovo. '
            + 'Le firme che ha in attesa vengono annullate.')) return;
        try {
            setErrore(null);
            const r = await api.delete(`/firma-tablet/dispositivi/${d.id}`);
            setAvviso(r.data.message);
            await carica();
        } catch (e) { setErrore(e?.message || 'Errore nella disattivazione'); }
    };

    const copia = async () => {
        try { await navigator.clipboard.writeText(creato.chiave); setCopiata(true); } catch (e) { /* si seleziona a mano */ }
    };

    return (
        <>
            <div className="dv-azioni-riga">
                <p className="dv-nota">
                    <b>La chiave si vede una volta sola</b>, alla creazione o alla rigenerazione: il
                    gestionale conserva solo la sua impronta. Un tablet che l'ha persa non la
                    recupera: si <b>rigenera</b>, si scrive la chiave nuova sul tablet, e il tablet
                    riparte. <b>Ultimo contatto</b> è l'ultima volta che il dispositivo ha chiesto
                    se c'era qualcosa da fare: un tablet acceso lo fa ogni quattro secondi.
                </p>
                {isAdmin && (
                    <button className="dv-btn dv-btn--principale"
                            onClick={() => { setNuovo({ nome: '', tipo: 'tablet', note: '' }); setErrore(null); }}>
                        <Plus size={16} /> Nuovo dispositivo
                    </button>
                )}
            </div>

            <div className="rm-scorre">
            <table className="dv-tabella">
                <thead>
                    <tr>
                        <th>Nome</th><th>Stato</th><th>Ultimo contatto</th>
                        <th>Firme in corso</th><th>Compilazione a metà</th>
                        <th>Timbratura</th><th />
                    </tr>
                </thead>
                <tbody>
                    {dispositivi.map((d) => {
                        const c = d.attivo ? contatto(d.ultima_attivita) : { testo: quando(d.ultima_attivita), classe: '' };
                        return (
                            <tr key={d.id} className={d.attivo ? '' : 'spento'}>
                                <td><b>{d.nome}</b><span className="dv-sub">{d.tipo}{d.note ? ` · ${d.note}` : ''}</span></td>
                                <td><span className={`dv-stato ${d.attivo ? 'attivo' : 'spento'}`}>{d.attivo ? 'attivo' : 'disattivato'}</span></td>
                                <td>
                                    <span className={`dv-contatto ${c.classe}`}>{c.testo}</span>
                                    <span className="dv-sub">{quando(d.ultima_attivita)}</span>
                                </td>
                                <td className="num">{d.in_corso}</td>
                                <td>
                                    {d.bozza_passo ? (
                                        <>
                                            <span className="dv-bozza">
                                                passaggio {d.bozza_passo} di 4
                                            </span>
                                            <span className="dv-sub">
                                                {d.bozza_paziente || 'accettazione da sola'}
                                                {' · '}{quando(d.bozza_aggiornata_il)}
                                            </span>
                                            {d.bozza_ripresa_chiesta && (
                                                <span className="dv-sub">rimandata al tablet</span>
                                            )}
                                        </>
                                    ) : '—'}
                                </td>
                                <td>{d.usa_timbratura ? 'abilitata' : '—'}</td>
                                <td className="num dv-celle-azioni">
                                    {/* Li vede anche la segreteria: e' chi ha la
                                        persona davanti, e aspettare un
                                        amministratore per far riprendere una
                                        compilazione vorrebbe dire non farla
                                        riprendere. */}
                                    {d.attivo && d.bozza_passo && (
                                        <>
                                            <button className="dv-link" onClick={() => riprendi(d)}>
                                                <RefreshCw size={14} /> Riprendi compilazione
                                            </button>
                                            <button className="dv-link dv-link--rosso" onClick={() => buttaBozza(d)}>
                                                <X size={14} /> Annulla compilazione
                                            </button>
                                        </>
                                    )}
                                    {d.attivo && isAdmin && (
                                        <>
                                            <button className="dv-link" onClick={() => { setRigenera(d); setErrore(null); }}>
                                                <RefreshCw size={14} /> Rigenera chiave
                                            </button>
                                            <button className="dv-link dv-link--rosso" onClick={() => disattiva(d)}>
                                                <Power size={14} /> Disattiva
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    {!dispositivi.length && <tr><td colSpan={6} className="dv-vuoto">Nessun dispositivo registrato.</td></tr>}
                </tbody>
            </table>
            </div>

            <p className="dv-nota">
                La timbratura si abilita da <b>Presenze → Tablet</b>: è una decisione dell'organico.
            </p>

            {nuovo && (
                <div className="dv-overlay" onClick={() => !inCorso && setNuovo(null)}>
                    <div className="dv-modale" onClick={(e) => e.stopPropagation()}>
                        <h2>Nuovo dispositivo</h2>
                        <label>Nome *
                            <input value={nuovo.nome} autoFocus placeholder="es. Tablet sala d'attesa"
                                   onChange={(e) => setNuovo({ ...nuovo, nome: e.target.value })} />
                        </label>
                        <label>Tipo
                            <select value={nuovo.tipo} onChange={(e) => setNuovo({ ...nuovo, tipo: e.target.value })}>
                                <option value="tablet">Tablet</option>
                                <option value="wacom">Tavoletta di firma</option>
                            </select>
                        </label>
                        <label>Note
                            <input value={nuovo.note} placeholder="dove sta, a cosa serve"
                                   onChange={(e) => setNuovo({ ...nuovo, note: e.target.value })} />
                        </label>
                        <div className="dv-modale-azioni">
                            <button className="dv-btn" disabled={inCorso} onClick={() => setNuovo(null)}>Annulla</button>
                            <button className="dv-btn dv-btn--principale" disabled={inCorso || !nuovo.nome.trim()} onClick={crea}>
                                {inCorso ? 'Creo…' : 'Crea e mostra la chiave'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {rigenera && (
                <div className="dv-overlay" onClick={() => !inCorso && setRigenera(null)}>
                    <div className="dv-modale" onClick={(e) => e.stopPropagation()}>
                        <h2><RefreshCw size={18} /> Rigenerare la chiave di «{rigenera.nome}»?</h2>
                        <div className="dv-avvertenza">
                            La chiave che il tablet ha adesso <b>smette di valere subito</b>: quel
                            tablet resta <b>fermo</b> finché qualcuno non gli scrive la chiave nuova —
                            fallo con il tablet a portata di mano, non con un paziente davanti.
                            <br /><br />
                            Gli altri dispositivi <b>non sono toccati</b>. La chiave nuova si vede
                            <b> una volta sola</b>, nella schermata che compare dopo.
                        </div>
                        <div className="dv-modale-azioni">
                            <button className="dv-btn" disabled={inCorso} onClick={() => setRigenera(null)}>Annulla</button>
                            <button className="dv-btn dv-btn--principale" disabled={inCorso} onClick={confermaRigenera}>
                                {inCorso ? 'Rigenero…' : 'Rigenera e mostra la chiave'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {creato && (
                <div className="dv-overlay">
                    <div className="dv-modale" onClick={(e) => e.stopPropagation()}>
                        <h2><KeyRound size={18} /> La chiave di «{creato.nome}»</h2>
                        <p className="dv-nota">
                            <b>Non verrà più mostrata.</b> Scrivila sul tablet adesso: apri
                            <code> https://rimedichub.it/tablet </code> e incollala nel campo che compare.
                            {creato.rigenerata && ' Finché non lo fai, quel tablet è fermo.'}
                        </p>
                        <div className="dv-chiave">
                            <code>{creato.chiave}</code>
                            <button className="dv-btn" onClick={copia}>
                                {copiata ? <><Check size={14} /> copiata</> : <><Copy size={14} /> copia</>}
                            </button>
                        </div>
                        <div className="dv-modale-azioni">
                            <button className="dv-btn dv-btn--principale" onClick={() => setCreato(null)}>
                                <X size={14} /> Ho scritto la chiave, chiudi
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

/* ---------- linguetta 2: compilazioni in sospeso ---------- */

const Compilazioni = ({ setErrore, setAvviso }) => {
    const [righe, setRighe] = useState([]);
    const [daCancellare, setDaCancellare] = useState(null);
    const [inCorso, setInCorso] = useState(false);

    const carica = useCallback(async () => {
        try {
            const r = await api.get('/firma-tablet/compilazioni');
            setRighe(r.data.data.compilazioni || []);
        } catch (e) { setErrore(e?.message || 'Non riesco a leggere le compilazioni.'); }
    }, [setErrore]);

    useEffect(() => { carica(); }, [carica]);

    const annulla = async (c) => {
        if (!window.confirm(`Annullare questa ${c.tipo === 'sessione' ? 'sessione' : c.tipo}? Il tablet si libera al giro successivo.`)) return;
        try {
            setErrore(null);
            if (c.tipo === 'sessione') await api.post(`/firma-tablet/sessioni/${c.id}/annulla`, { motivo: 'Annullata dalla schermata Tablet' });
            else if (c.tipo === 'accettazione') await api.post(`/firma-tablet/anagrafiche/${c.id}/scarta`, { motivo: 'Scartata dalla schermata Tablet' });
            else await api.post(`/questionari/compilazione/${c.id}/annulla`, { motivo: 'Annullata dalla schermata Tablet' });
            setAvviso('Annullata: il tablet si libera al prossimo giro.');
            await carica();
        } catch (e) { setErrore(e?.message || 'Errore nell\'annullamento'); }
    };

    const cancella = async () => {
        const c = daCancellare;
        try {
            setErrore(null); setInCorso(true);
            const r = await api.delete(`/firma-tablet/compilazioni/${c.tipo}/${c.id}`);
            setAvviso(r.data.message); setDaCancellare(null);
            await carica();
        } catch (e) { setErrore(e?.message || 'Errore nella cancellazione'); } finally { setInCorso(false); }
    };

    const annullate = righe.filter((c) => !APERTE.includes(c.stato));
    const cancellaTutte = async () => {
        if (!window.confirm(`Togliere ${annullate.length} compilazioni annullate, scartate o scadute? Nel registro resta chi e quando, non il contenuto.`)) return;
        try {
            setErrore(null); setInCorso(true);
            const r = await api.delete('/firma-tablet/compilazioni-annullate', { data: { conferma: annullate.length } });
            setAvviso(r.data.message);
            await carica();
        } catch (e) { setErrore(e?.message || 'Errore nella cancellazione'); } finally { setInCorso(false); }
    };

    return (
        <>
            <div className="dv-azioni-riga">
                <p className="dv-nota">
                    Le sessioni <b>aperte</b> sono quelle che tengono un tablet fermo: <b>Annulla</b> lo
                    libera al giro di polling successivo. Le compilazioni <b>vuote</b> abbandonate
                    scadono da sole dopo 48 ore; quelle <b>con dati</b> non si toccano mai da sole, e si
                    cancellano solo di proposito. Le accettazioni <b>confermate</b> non sono qui: sono
                    anagrafiche.
                </p>
                {annullate.length > 0 && (
                    <button className="dv-btn" disabled={inCorso} onClick={cancellaTutte}>
                        <Trash2 size={14} /> Togli le {annullate.length} annullate
                    </button>
                )}
            </div>

            <div className="rm-scorre">
            <table className="dv-tabella">
                <thead>
                    <tr><th>Cosa</th><th>Paziente</th><th>Dispositivo</th><th>Inizio</th><th>Stato</th><th>Dati</th><th /></tr>
                </thead>
                <tbody>
                    {righe.map((c) => (
                        <tr key={`${c.tipo}-${c.id}`} className={APERTE.includes(c.stato) ? 'aperta' : ''}>
                            <td><b>{c.tipo === 'sessione' ? `sessione ${c.sottotipo}` : c.tipo === 'accettazione' ? `accettazione ${c.sottotipo}` : `questionario ${c.sottotipo || ''}`}</b>
                                {c.titolo && <span className="dv-sub">{c.titolo}</span>}</td>
                            <td>{c.cognome ? `${c.cognome} ${c.paziente_nome || ''}` : <span className="dv-sub">—</span>}</td>
                            <td>{c.dispositivo || '—'}</td>
                            <td>{quando(c.inizio)}</td>
                            <td><span className={`dv-stato ${APERTE.includes(c.stato) ? 'aperta' : ''}`}>{STATO_COMP[c.stato] || c.stato}</span></td>
                            <td>{c.ha_dati ? <b>con dati</b> : <span className="dv-sub">vuota</span>}</td>
                            <td className="num dv-celle-azioni">
                                {APERTE.includes(c.stato) ? (
                                    <button className="dv-link dv-link--rosso" onClick={() => annulla(c)}><Ban size={14} /> Annulla</button>
                                ) : (
                                    <button className="dv-link dv-link--rosso" onClick={() => { setDaCancellare(c); setErrore(null); }}><Trash2 size={14} /> Cancella</button>
                                )}
                            </td>
                        </tr>
                    ))}
                    {!righe.length && <tr><td colSpan={7} className="dv-vuoto">Niente in sospeso.</td></tr>}
                </tbody>
            </table>
            </div>

            {daCancellare && (
                <div className="dv-overlay" onClick={() => !inCorso && setDaCancellare(null)}>
                    <div className="dv-modale" onClick={(e) => e.stopPropagation()}>
                        <h2><Trash2 size={18} /> Cancellare questa compilazione?</h2>
                        {daCancellare.ha_dati ? (
                            <div className="dv-avvertenza">
                                <b>Contiene dati scritti da un paziente.</b> Cancellandola non si
                                recuperano: nel registro resterà <b>chi</b> l'ha cancellata e
                                <b> quando</b>, non che cosa conteneva.
                            </div>
                        ) : (
                            <p className="dv-nota">È vuota: non c'è niente da perdere. Nel registro resta chi e quando.</p>
                        )}
                        <div className="dv-modale-azioni">
                            <button className="dv-btn" disabled={inCorso} onClick={() => setDaCancellare(null)}>Annulla</button>
                            <button className="dv-btn dv-btn--rosso" disabled={inCorso} onClick={cancella}>
                                {inCorso ? 'Cancello…' : daCancellare.ha_dati ? 'Cancella, ho capito' : 'Cancella'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

/* ---------- la pagina ---------- */

const PaginaTablet = () => {
    const user = useAuthStore((s) => s.user);
    const isAdmin = user?.ruolo === 'admin';
    const [linguetta, setLinguetta] = useState('dispositivi');
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);

    return (
        <div className="dv-wrap">
            <header className="dv-testata">
                <div>
                    <h1><Tablet size={20} /> Tablet</h1>
                    <p>I dispositivi della firma e delle accettazioni, e quello che hanno lasciato a metà.</p>
                </div>
            </header>
            <nav className="dv-linguette">
                <button className={linguetta === 'dispositivi' ? 'attiva' : ''} onClick={() => setLinguetta('dispositivi')}>
                    <Tablet size={15} /> Dispositivi
                </button>
                <button className={linguetta === 'compilazioni' ? 'attiva' : ''} onClick={() => setLinguetta('compilazioni')}>
                    <ListChecks size={15} /> Compilazioni in sospeso
                </button>
            </nav>
            {errore && <div className="dv-errore">{errore}</div>}
            {avviso && <div className="dv-avviso" onClick={() => setAvviso(null)}>{avviso}</div>}
            {linguetta === 'dispositivi'
                ? <Dispositivi isAdmin={isAdmin} setErrore={setErrore} setAvviso={setAvviso} />
                : <Compilazioni setErrore={setErrore} setAvviso={setAvviso} />}
        </div>
    );
};

export default PaginaTablet;
